import { useRef, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

export interface SegmentedOption {
  value: string;
  label: string;
}

interface SegmentedProps {
  /** 2–4 options. */
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  /** Accessible name of the radio group. */
  label: string;
  className?: string;
}

/** Segmented control: a radiogroup with roving focus; arrow keys / Home / End move and select. */
export function Segmented({ options, value, onChange, label, className }: SegmentedProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  );

  const select = (index: number) => {
    const next = (index + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const moves: Record<string, number> = {
      ArrowRight: index + 1,
      ArrowDown: index + 1,
      ArrowLeft: index - 1,
      ArrowUp: index - 1,
      Home: 0,
      End: options.length - 1,
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    select(moves[event.key]);
  };

  return (
    <div role="radiogroup" aria-label={label} className={cn("inline-flex rounded-lg bg-ds-sunk p-0.5 font-ds", className)}>
      {options.map((option, index) => {
        const checked = index === selectedIndex;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              "h-7 whitespace-nowrap rounded-md px-2.5 text-[12.5px] font-semibold transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent",
              checked ? "bg-ds-panel text-ds-ink shadow-sm" : "text-ds-ink2 hover:text-ds-ink"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
